import React, { useState } from 'react';
import { Button, Icon, Spinner } from '@/components/atoms';
import { Modal } from '@/components/molecules/Modal/Modal';
import { FileUpload } from '@/components/molecules/FileUpload';
import { AttachmentViewer } from './AttachmentViewer';
import { useAttachmentsList, useDeleteAttachmentMutation } from '@/hooks/useErpApi';
import type { EntityType } from '@/schemas/attachments';
import { getFileExtension } from '@/schemas/attachments';

// API response type from backend
interface AttachmentResponse {
  id: string;
  entity_type: string;
  entity_id: string;
  file_path: string;
  file_type: string;
  download_url: string;
  uploaded_by: string;
  uploaded_at: string;
  created_at: string;
  updated_at: string;
}

// Helper to extract filename from file_path
const getFilenameFromPath = (filePath: string): string => {
  const parts = filePath.split('/');
  return parts[parts.length - 1] || filePath;
};

export interface AttachmentListProps {
  entityType: EntityType;
  entityId: string;
  allowUpload?: boolean;
  allowDelete?: boolean;
  viewMode?: 'list' | 'grid';
}

const getFileIcon = (fileType: string): 'Image' | 'FileText' | 'Table' | 'File' => {
  if (fileType.startsWith('image/')) return 'Image';
  if (fileType === 'application/pdf') return 'FileText';
  if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType === 'text/csv')
    return 'Table';
  if (fileType.includes('word') || fileType.includes('document')) return 'FileText';
  return 'File';
};

export const AttachmentList: React.FC<AttachmentListProps> = ({
  entityType,
  entityId,
  allowUpload = true,
  allowDelete = true,
  viewMode = 'list',
}) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [viewerAttachment, setViewerAttachment] = useState<AttachmentResponse | null>(null);
  const [sortBy, setSortBy] = useState<'name' | 'size' | 'date'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { data: attachments, isLoading, error, refetch } = useAttachmentsList(entityType, entityId);
  const { mutateAsync: deleteAttachment } = useDeleteAttachmentMutation();

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    refetch();
  };

  const handleDelete = async (attachment: AttachmentResponse) => {
    const filename = getFilenameFromPath(attachment.file_path);
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }

    try {
      await deleteAttachment(attachment.id);
      refetch();
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      alert('Failed to delete attachment. Please try again.');
    }
  };

  const handleDownload = async (attachment: AttachmentResponse) => {
    try {
      // Open the presigned download URL in a new tab
      window.open(attachment.download_url, '_blank');
    } catch (error) {
      console.error('Failed to download attachment:', error);
      alert('Failed to download attachment. Please try again.');
    }
  };

  const sortedAttachments = React.useMemo(() => {
    if (!attachments) return [];

    // Cast from API type to our local type - they should be compatible at runtime
    const typedAttachments = attachments as unknown as AttachmentResponse[];

    const sorted = [...typedAttachments].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = getFilenameFromPath(a.file_path).localeCompare(
            getFilenameFromPath(b.file_path)
          );
          break;
        case 'size':
          // Size not available in API response, sort by date instead
          comparison = new Date(a.uploaded_at).getTime() - new Date(b.uploaded_at).getTime();
          break;
        case 'date':
          comparison = new Date(a.uploaded_at).getTime() - new Date(b.uploaded_at).getTime();
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [attachments, sortBy, sortOrder]);

  const toggleSort = (field: 'name' | 'size' | 'date') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Spinner size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-50 border border-error-200 rounded-lg p-4">
        <p className="text-error-700">Failed to load attachments. Please try again.</p>
      </div>
    );
  }

  if (!attachments || attachments.length === 0) {
    return (
      <>
        <div className="text-center py-8 border-2 border-dashed border-neutral-300 rounded-lg">
          <div className="w-16 h-16 mx-auto bg-neutral-100 rounded-full flex items-center justify-center mb-4">
            <Icon name="File" size="lg" className="text-neutral-400" />
          </div>
          <p className="text-neutral-600 mb-4">No attachments yet</p>
          {allowUpload && (
            <Button variant="primary" onClick={() => setShowUploadModal(true)}>
              Upload Files
            </Button>
          )}
        </div>

        {/* Upload Modal - must be included in empty state too */}
        {showUploadModal && (
          <Modal
            isOpen={showUploadModal}
            onClose={() => setShowUploadModal(false)}
            title="Upload Files"
            size="lg"
          >
            <FileUpload
              entityType={entityType}
              entityId={entityId}
              multiple={true}
              onSuccess={() => {
                setShowUploadModal(false);
                refetch();
              }}
              onError={(error) => alert(error)}
            />
          </Modal>
        )}
      </>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-neutral-900">
          Attachments ({sortedAttachments.length})
        </h3>
        {allowUpload && (
          <Button variant="primary" onClick={() => setShowUploadModal(true)}>
            <Icon name="Upload" size="sm" />
            <span className="ml-2">Upload Files</span>
          </Button>
        )}
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-700">
                  <button
                    onClick={() => toggleSort('name')}
                    className="flex items-center gap-1 hover:text-neutral-900"
                  >
                    File Name
                    {sortBy === 'name' && (
                      <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size="sm" />
                    )}
                  </button>
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-700">Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-700">
                  <button
                    onClick={() => toggleSort('date')}
                    className="flex items-center gap-1 hover:text-neutral-900"
                  >
                    Upload Date
                    {sortBy === 'date' && (
                      <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size="sm" />
                    )}
                  </button>
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-neutral-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {sortedAttachments.map((attachment) => {
                const filename = getFilenameFromPath(attachment.file_path);
                return (
                  <tr key={attachment.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center overflow-hidden">
                          {attachment.file_type.startsWith('image/') ? (
                            <img
                              src={attachment.download_url}
                              alt={filename}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Icon
                              name={getFileIcon(attachment.file_type)}
                              size="sm"
                              className="text-neutral-600"
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-neutral-900 truncate">
                            {filename}
                          </p>
                          <p className="text-xs text-neutral-500">
                            {getFileExtension(filename).toUpperCase()}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-700">{attachment.file_type}</td>
                    <td className="px-4 py-3 text-sm text-neutral-700">
                      {new Date(attachment.uploaded_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setViewerAttachment(attachment)}
                          className="p-1 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded"
                          aria-label="View attachment"
                        >
                          <Icon name="Eye" size="sm" />
                        </button>
                        <button
                          onClick={() => handleDownload(attachment)}
                          className="p-1 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded"
                          aria-label="Download attachment"
                        >
                          <Icon name="Download" size="sm" />
                        </button>
                        {allowDelete && (
                          <button
                            onClick={() => handleDelete(attachment)}
                            className="p-1 text-neutral-600 hover:text-error-600 hover:bg-error-50 rounded"
                            aria-label="Delete attachment"
                          >
                            <Icon name="Trash" size="sm" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {sortedAttachments.map((attachment) => {
            const filename = getFilenameFromPath(attachment.file_path);
            const isImage = attachment.file_type.startsWith('image/');
            return (
              <div
                key={attachment.id}
                className="border border-neutral-200 rounded-lg overflow-hidden hover:border-primary-300 hover:shadow-md transition-all"
              >
                {/* Image Preview or Icon */}
                <div className="aspect-square bg-neutral-100 flex items-center justify-center overflow-hidden">
                  {isImage ? (
                    <img
                      src={attachment.download_url}
                      alt={filename}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => setViewerAttachment(attachment)}
                    />
                  ) : (
                    <Icon
                      name={getFileIcon(attachment.file_type)}
                      size="lg"
                      className="text-neutral-400"
                    />
                  )}
                </div>

                {/* File Info */}
                <div className="p-3">
                  <p
                    className="text-sm font-medium text-neutral-900 truncate mb-1"
                    title={filename}
                  >
                    {filename}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {new Date(attachment.uploaded_at).toLocaleDateString()}
                  </p>

                  {/* Actions */}
                  <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-neutral-200">
                    <button
                      onClick={() => setViewerAttachment(attachment)}
                      className="text-xs text-primary-600 hover:text-primary-700"
                    >
                      View
                    </button>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleDownload(attachment)}
                        className="p-1 text-neutral-600 hover:text-primary-600"
                        aria-label="Download"
                      >
                        <Icon name="Download" size="sm" />
                      </button>
                      {allowDelete && (
                        <button
                          onClick={() => handleDelete(attachment)}
                          className="p-1 text-neutral-600 hover:text-error-600"
                          aria-label="Delete"
                        >
                          <Icon name="Trash" size="sm" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <Modal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          title="Upload Files"
          size="lg"
        >
          <FileUpload
            entityType={entityType}
            entityId={entityId}
            multiple={true}
            onSuccess={handleUploadSuccess}
            onError={(error) => alert(error)}
          />
        </Modal>
      )}

      {/* Attachment Viewer */}
      {viewerAttachment && (
        <AttachmentViewer
          attachment={viewerAttachment}
          open={!!viewerAttachment}
          onClose={() => setViewerAttachment(null)}
        />
      )}
    </div>
  );
};
